/**
 * CypAsdCriterionPanel — Main content panel for a single DSM-5 ASD criterion.
 *
 * Layout — three-column design for each criterion:
 *   Column 1 (reference): Category subheadings (e.g. "Eye contact") with
 *            collapsible probing questions underneath for clinician guidance.
 *   Column 2: Single notes box for Age 4–5, with an
 *            evidence dropdown in the column header.
 *   Column 3: Single notes box for Current, with an evidence dropdown in
 *            the column header.
 *
 * This layout lets the clinician see the reference prompts on the left while
 * typing freely into the early/current notes side by side, mirroring how
 * the report narrative is structured (historical vs present-day per criterion).
 *
 * Categories from both stages are merged into a unified reference list in
 * column 1. The notes columns each store a single free-text field per
 * criterion stage.
 */

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, XIcon, SearchIcon } from '@heroicons/react/solid';
import StructuredNotesEditor, { PROBING_Q_MIME, DOMAIN_MIME } from '../../12_observation/components/StructuredNotesEditor';
import { moveNoteBetweenArrays } from '../../../../utils/structuredNotesMove';
import InfoTooltip from '../../../../components/InfoTooltip';
import { EVIDENCE_OPTIONS, SCORING_NOTES, FUNCTIONAL_IMPAIRMENT_DOMAINS } from '../config/cypAsdSSIConfig';

/**
 * Smoothly animates the expand/collapse of a probing-questions category.
 * Uses a measured scrollHeight approach so the CSS transition has a concrete
 * max-height target, avoiding the common "transition to auto" problem.
 */
function CollapsibleCategory({ category, isExpanded, onToggle, askedQuestions, onToggleAsked }) {
  const contentRef = useRef(null);
  const innerRef = useRef(null);
  const [measuredHeight, setMeasuredHeight] = useState(0);

  /**
   * Use a ResizeObserver on the *inner* wrapper (not the overflow-hidden
   * container) so we continuously track its natural height.  This catches
   * every source of resize — text wrapping at different zoom levels, the
   * "Marked as asked" CSS transition completing, window resize, etc. —
   * without needing to guess timing or add arbitrary buffers.
   */
  useEffect(() => {
    const inner = innerRef.current;
    if (!inner) return;

    const ro = new ResizeObserver(() => {
      setMeasuredHeight(inner.scrollHeight);
    });
    ro.observe(inner);

    // Seed the initial value
    setMeasuredHeight(inner.scrollHeight);

    return () => ro.disconnect();
  }, [category]);

  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors duration-150 text-left"
      >
        <ChevronDownIcon
          className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${
            isExpanded ? 'rotate-180' : 'rotate-0'
          }`}
        />
        <span className="text-sm font-semibold text-slate-700">{category.title}</span>
      </button>

      <div
        ref={contentRef}
        className="overflow-hidden transition-all duration-300"
        style={{
          maxHeight: isExpanded ? `${measuredHeight}px` : '0px',
          opacity: isExpanded ? 1 : 0,
          transitionTimingFunction: isExpanded
            ? 'cubic-bezier(0.4, 0, 0.2, 1)'
            : 'cubic-bezier(0.4, 0, 1, 1)',
        }}
      >
        <div ref={innerRef} className="ml-6 mt-1 mb-2">
          {category.stages.map((stageGroup) => (
            <div key={stageGroup.label} className="mb-2 last:mb-0">
              {category.stages.length > 1 && (
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                  {stageGroup.label}
                </div>
              )}
              <div className="flex flex-col gap-1.5">
                {stageGroup.questions.map((q, i) => {
                  const isAsked = askedQuestions?.has(q);
                  return (
                    <div
                      key={i}
                      draggable="true"
                      onDragStart={(e) => {
                        e.dataTransfer.setData(PROBING_Q_MIME, q);
                        e.dataTransfer.setData('text/plain', q);
                        e.dataTransfer.effectAllowed = 'copy';
                      }}
                      onClick={() => onToggleAsked?.(q)}
                      className={`
                        px-2 py-1.5 rounded-lg text-xs font-medium italic leading-relaxed
                        cursor-pointer select-none transition-all duration-200
                        ${isAsked
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-800 hover:bg-blue-50/60'}
                      `}
                      title={isAsked ? 'Click to unmark' : 'Click to mark as asked — or drag into notes'}
                    >
                      <div className="flex items-start gap-2.5">
                        <span className={`text-sm leading-relaxed flex-shrink-0 text-gray-400`}>•</span>
                        <span>&ldquo;{q}&rdquo;</span>
                      </div>
                      {/*
                        * "Marked as asked" label — always in the DOM so the
                        * ResizeObserver can track its height continuously.
                        *
                        * Uses a CSS grid row trick for smooth height animation:
                        * grid-template-rows transitions from 0fr → 1fr, which
                        * the browser can interpolate natively.  This avoids the
                        * max-height guessing game and produces a true
                        * content-aware expand/collapse in both directions.
                        */}
                      <div
                        className="grid transition-all duration-300 ease-in-out"
                        style={{
                          gridTemplateRows: isAsked ? '1fr' : '0fr',
                          opacity: isAsked ? 1 : 0,
                          marginTop: isAsked ? '0.25rem' : '0px',
                        }}
                      >
                        <div className="overflow-hidden">
                          <span className="text-[10px] font-semibold text-blue-500 pl-5 not-italic block pb-0.5">
                            Marked as asked
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Maps each evidence level to a colour scheme used by both the dropdown and
 * button variants. The colours give instant visual feedback on the strength
 * of evidence recorded, following a traffic-light metaphor:
 *   green  → clear evidence
 *   amber  → partial evidence
 *   red    → no evidence
 *   grey   → inconclusive
 */
const EVIDENCE_COLOURS = {
  'Clear evidence':   { bg: 'bg-emerald-50',  text: 'text-emerald-700', border: 'border-emerald-300', ring: 'ring-emerald-200', selectedBg: 'bg-emerald-600', selectedBorder: 'border-emerald-600', hoverBorder: 'hover:border-emerald-400' },
  'Partial evidence': { bg: 'bg-amber-50',    text: 'text-amber-700',   border: 'border-amber-300',   ring: 'ring-amber-200',   selectedBg: 'bg-amber-500',   selectedBorder: 'border-amber-500',   hoverBorder: 'hover:border-amber-400' },
  'No evidence':      { bg: 'bg-red-50',      text: 'text-red-700',     border: 'border-red-300',     ring: 'ring-red-200',     selectedBg: 'bg-red-500',     selectedBorder: 'border-red-500',     hoverBorder: 'hover:border-red-400' },
  'Inconclusive':     { bg: 'bg-gray-100',    text: 'text-gray-600',    border: 'border-gray-400',    ring: 'ring-gray-200',    selectedBg: 'bg-gray-500',    selectedBorder: 'border-gray-500',    hoverBorder: 'hover:border-gray-400' },
};

export default function CypAsdCriterionPanel({
  criterion,
  criterionData,
  onFieldChange,
  onNavigate,
  criteria,
  displayPreferences = {},
}) {
  const val = (fieldId) => criterionData?.[fieldId] ?? null;

  /**
   * Moves a note card between the EDP and Current notes boxes. The domainId
   * used by each StructuredNotesEditor is the field key ('notes_early' or
   * 'notes_current'), so the source/target map directly to those keys.
   */
  const handleMoveNote = useCallback((sourceFieldKey, noteId, targetFieldKey, insertAt) => {
    const result = moveNoteBetweenArrays(val(sourceFieldKey), val(targetFieldKey), noteId, insertAt);
    if (!result) return;
    onFieldChange(sourceFieldKey, result.source);
    onFieldChange(targetFieldKey, result.target);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criterionData, onFieldChange]);

  const availableCriteria = criteria.filter(c => c.available);
  const currentIdx = availableCriteria.findIndex(c => c.id === criterion.id);
  const prevCriterion = currentIdx > 0 ? availableCriteria[currentIdx - 1] : null;
  const nextCriterion = currentIdx < availableCriteria.length - 1
    ? availableCriteria[currentIdx + 1]
    : null;

  const tooltipParts = [];
  if (SCORING_NOTES[criterion.criterionGroup]) {
    tooltipParts.push(SCORING_NOTES[criterion.criterionGroup]);
  }
  if (criterion.description) {
    tooltipParts.push(criterion.description);
  }
  const tooltipContent = tooltipParts.join('\n\n');

  /**
   * Merge categories from both stages into a single reference list.
   * Each entry carries probing questions grouped by which stage(s) they
   * belong to, so the clinician sees all relevant prompts in one place.
   */
  const mergedCategories = useMemo(() => {
    const earlyStage = criterion.stages.find(s => s.id === 'early');
    const currentStage = criterion.stages.find(s => s.id === 'current');
    const result = [];
    const seenTitles = new Set();

    const addCategories = (stage, stageLabel) => {
      if (!stage) return;
      stage.categories.forEach(cat => {
        const normalised = cat.title.toLowerCase().replace(/[^a-z]/g, '');
        const existing = result.find(
          r => r.normalisedTitle === normalised
        );
        if (existing) {
          existing.stages.push({ label: stageLabel, questions: cat.probingQuestions });
        } else {
          seenTitles.add(normalised);
          result.push({
            title: cat.title,
            normalisedTitle: normalised,
            stages: [{ label: stageLabel, questions: cat.probingQuestions }],
          });
        }
      });
    };

    addCategories(earlyStage, 'Early');
    addCategories(currentStage, 'Current');
    return result;
  }, [criterion]);

  /**
   * Track which category sections are expanded in column 1.
   * Default: first category expanded, rest collapsed.
   */
  const [expandedCategories, setExpandedCategories] = useState(() => {
    if (mergedCategories.length > 0) {
      return new Set([mergedCategories[0].normalisedTitle]);
    }
    return new Set();
  });

  /**
   * Accordion behaviour — only one category can be expanded at a time.
   * Clicking the already-open category collapses it; clicking a different
   * one swaps smoothly (the old one collapses while the new one expands
   * in the same animation frame, keeping vertical movement minimal).
   */
  const toggleCategory = (normalisedTitle) => {
    setExpandedCategories(prev => {
      if (prev.has(normalisedTitle)) {
        return new Set();
      }
      return new Set([normalisedTitle]);
    });
  };

  const evidenceMode = displayPreferences.evidenceMode || 'dropdown';
  const navMode = displayPreferences.navMode || 'bottom';

  /**
   * Tracks which probing questions the clinician has marked as "asked".
   * Stored as an array in criterionData so it persists when navigating
   * between criteria. Converted to a Set for O(1) lookup in rendering.
   */
  const askedQuestionsSet = useMemo(
    () => new Set(val('asked_questions') || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [criterionData]
  );

  const toggleAskedQuestion = useCallback((question) => {
    const current = val('asked_questions') || [];
    const next = current.includes(question)
      ? current.filter(q => q !== question)
      : [...current, question];
    onFieldChange('asked_questions', next);
  }, [onFieldChange, criterionData]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Popup showing all probing questions marked as asked across categories. */
  const [showAskedPopup, setShowAskedPopup] = useState(false);
  const askedQuestionsArray = useMemo(() => val('asked_questions') || [], [criterionData]); // eslint-disable-line react-hooks/exhaustive-deps

  const earlyStage = criterion.stages.find(s => s.id === 'early');
  const currentStage = criterion.stages.find(s => s.id === 'current');
  const earlyEvidence = val('evidence_early') || '';
  const currentEvidence = val('evidence_current') || '';

  /**
   * Renders the evidence selector for a life stage — either a <select> dropdown
   * or a row of single-select buttons depending on the user's display preference.
   * Both modes use semantic colour coding when a selection is active.
   */
  const renderEvidenceSelector = useCallback((currentValue, stageFieldId, ariaLabel) => {
    const colours = EVIDENCE_COLOURS[currentValue];

    if (evidenceMode === 'buttons') {
      return (
        <div className="flex flex-wrap gap-1.5">
          {EVIDENCE_OPTIONS.map(opt => {
            const isSelected = currentValue === opt;
            const optColours = EVIDENCE_COLOURS[opt];
            return (
              <button
                key={opt}
                type="button"
                onClick={() => onFieldChange(stageFieldId, isSelected ? null : opt)}
                className={`
                  px-2.5 py-1 rounded-lg text-xs font-semibold border transition-all duration-150
                  ${isSelected
                    ? `${optColours.selectedBg} text-white ${optColours.selectedBorder} shadow-sm`
                    : `bg-white text-gray-600 border-gray-300 ${optColours.hoverBorder} hover:${optColours.text}`}
                `}
                aria-label={`${opt} — ${ariaLabel}`}
                aria-pressed={isSelected}
              >
                {opt}
              </button>
            );
          })}
        </div>
      );
    }

    const dropdownClasses = colours
      ? `px-2.5 py-1 rounded-lg text-xs font-semibold border shadow-sm focus:outline-none focus:ring-2 min-w-[130px] transition-all duration-150 ${colours.bg} ${colours.text} ${colours.border} focus:${colours.ring}`
      : 'px-2.5 py-1 rounded-lg text-xs font-semibold border border-gray-400 bg-white text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 min-w-[130px]';

    return (
      <div className="flex items-center gap-1.5">
        <span className="text-xs font-medium text-gray-500 whitespace-nowrap">Evidence:</span>
        <select
          value={currentValue}
          onChange={(e) => onFieldChange(stageFieldId, e.target.value || null)}
          className={dropdownClasses}
          aria-label={ariaLabel}
        >
          <option value="">Select</option>
          {EVIDENCE_OPTIONS.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    );
  }, [evidenceMode, onFieldChange]);

  // -------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------

  return (
    <div className="flex flex-col h-full text-left">
    <div className="flex-1 min-h-0 overflow-y-auto p-6">
      {/* Top navigation — only shown when navMode is 'both' */}
      {navMode === 'both' && (
        <div className="flex justify-between items-center mb-4">
          {prevCriterion ? (
            <button
              onClick={() => onNavigate(prevCriterion.id)}
              className="flex items-center py-2 px-4 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 hover:bg-gray-50"
            >
              <ChevronLeftIcon className="w-5 h-5 mr-2" />
              <span className="font-bold text-sm">{prevCriterion.id}</span>
            </button>
          ) : <div />}
          {nextCriterion ? (
            <button
              onClick={() => onNavigate(nextCriterion.id)}
              className="flex items-center py-2 px-4 rounded-md shadow-md bg-primary text-white hover:bg-primary/90 transition-all duration-300"
            >
              <span className="font-bold text-sm">Next: {nextCriterion.id}</span>
              <ChevronRightIcon className="w-5 h-5 ml-2" />
            </button>
          ) : (
            <button
              onClick={() => onNavigate('REVIEW')}
              className="flex items-center py-2 px-4 rounded-md shadow-md bg-primary text-white hover:bg-primary/90 transition-all duration-300"
            >
              <span className="font-bold text-sm">Go to Review</span>
              <ChevronRightIcon className="w-5 h-5 ml-2" />
            </button>
          )}
        </div>
      )}

      {/* Criterion title — short name as primary heading, full description below */}
      <div className="mb-6 text-left">
        <div className="flex items-center gap-2">
          <h1 className="text-gray-900 text-3xl font-bold">
            {criterion.id}. {criterion.shortTitle}
          </h1>
          {tooltipContent && (
            <InfoTooltip
              content={tooltipContent}
              label={`${criterion.id} details`}
              maxWidth="380px"
            />
          )}
        </div>
        <p className="text-gray-500 text-sm mt-1 font-semibold">
          {criterion.fullTitle.replace(/^[AB]\d+\.\s*/, '')}
        </p>
      </div>

      {/* Three-column layout: Reference | Early Notes | Current Notes */}
      <div className="flex gap-4 items-start">

        {/*
         * Column 1 — Probing questions reference.
         * Sticky so it stays visible as the clinician scrolls through
         * lengthy notes. Has its own internal scroll if content exceeds
         * the viewport height minus some padding for the heading area.
         */}
        <div className="w-[320px] flex-shrink-0 bg-gray-50 rounded-2xl border border-gray-200 p-4 sticky top-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-slate-800 text-lg font-bold leading-tight text-left">
              Probing Questions
            </h2>
            {askedQuestionsArray.length > 0 && (
              <button
                onClick={() => setShowAskedPopup(true)}
                className="p-1 rounded-full text-gray-400
                  hover:text-primary hover:bg-primary/10
                  transition-colors duration-200"
                title="View questions marked as asked"
              >
                <SearchIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="flex flex-col gap-1">
            {mergedCategories.map((cat) => (
              <CollapsibleCategory
                key={cat.normalisedTitle}
                category={cat}
                isExpanded={expandedCategories.has(cat.normalisedTitle)}
                onToggle={() => toggleCategory(cat.normalisedTitle)}
                askedQuestions={askedQuestionsSet}
                onToggleAsked={toggleAskedQuestion}
              />
            ))}
          </div>
        </div>

        {/* Column 2 — Age 4–5 */}
        {earlyStage && (
          <div className="flex-1 flex flex-col bg-gray-50 rounded-2xl border border-gray-200 p-4">
            <h2 className="text-slate-800 text-lg font-bold leading-tight text-left mb-3">
              {earlyStage.title}
            </h2>

            {/* Symptom Present */}
            <h3 className="text-slate-600 text-sm font-semibold text-left mb-1.5">Threshold met:<span className="text-red-500 ml-0.5">*</span></h3>
            <div className="mb-3">
              {renderEvidenceSelector(earlyEvidence, 'evidence_early', 'Evidence for Age 4–5')}
            </div>

            {/* Domains Impacted — multi-select, draggable into notes */}
            <h3 className="text-slate-600 text-sm font-semibold text-left mb-1.5">Domains Impacted:</h3>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {FUNCTIONAL_IMPAIRMENT_DOMAINS.map(domain => {
                const selected = (val('domains_early') || []);
                const isSelected = selected.includes(domain.id);
                const domainLabel = `Domain Impacted: ${domain.title} `;
                return (
                  <button
                    key={domain.id}
                    type="button"
                    draggable="true"
                    onDragStart={(e) => {
                      e.dataTransfer.setData(DOMAIN_MIME, domainLabel);
                      e.dataTransfer.setData('text/plain', domainLabel);
                      e.dataTransfer.effectAllowed = 'copy';
                    }}
                    onClick={() => {
                      const next = isSelected
                        ? selected.filter(d => d !== domain.id)
                        : [...selected, domain.id];
                      onFieldChange('domains_early', next);
                    }}
                    className={`
                      px-2.5 py-1 rounded-lg text-xs font-semibold border transition-all duration-150 cursor-grab active:cursor-grabbing
                      ${isSelected
                        ? 'bg-primary text-white border-primary shadow-sm'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-primary/40 hover:text-primary'}
                    `}
                    aria-pressed={isSelected}
                    title="Click to select/deselect — drag into notes to create a domain note"
                  >
                    {domain.title}
                  </button>
                );
              })}
            </div>

            {/* Notes — structured note cards with quote detection and drag-and-drop */}
            <div className="flex-1">
              <StructuredNotesEditor
                key={`${criterion.id}_early`}
                notes={val('notes_early') || []}
                onChange={(v) => onFieldChange('notes_early', v)}
                placeholder="Type a note and press Enter…"
                minHeight="15rem"
                domainId="notes_early"
                onMoveNoteIn={handleMoveNote}
              />
            </div>
          </div>
        )}

        {/* Column 3 — Current */}
        {currentStage && (
          <div className="flex-1 flex flex-col bg-gray-50 rounded-2xl border border-gray-200 p-4">
            <h2 className="text-slate-800 text-lg font-bold leading-tight text-left mb-3">
              {currentStage.title}
            </h2>

            {/* Symptom Present */}
            <h3 className="text-slate-600 text-sm font-semibold text-left mb-1.5">Threshold met:<span className="text-red-500 ml-0.5">*</span></h3>
            <div className="mb-3">
              {renderEvidenceSelector(currentEvidence, 'evidence_current', 'Evidence for Current')}
            </div>

            {/* Domains Impacted — multi-select, draggable into notes */}
            <h3 className="text-slate-600 text-sm font-semibold text-left mb-1.5">Domains Impacted:</h3>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {FUNCTIONAL_IMPAIRMENT_DOMAINS.map(domain => {
                const selected = (val('domains_current') || []);
                const isSelected = selected.includes(domain.id);
                const domainLabel = `Domain Impacted: ${domain.title} `;
                return (
                  <button
                    key={domain.id}
                    type="button"
                    draggable="true"
                    onDragStart={(e) => {
                      e.dataTransfer.setData(DOMAIN_MIME, domainLabel);
                      e.dataTransfer.setData('text/plain', domainLabel);
                      e.dataTransfer.effectAllowed = 'copy';
                    }}
                    onClick={() => {
                      const next = isSelected
                        ? selected.filter(d => d !== domain.id)
                        : [...selected, domain.id];
                      onFieldChange('domains_current', next);
                    }}
                    className={`
                      px-2.5 py-1 rounded-lg text-xs font-semibold border transition-all duration-150 cursor-grab active:cursor-grabbing
                      ${isSelected
                        ? 'bg-primary text-white border-primary shadow-sm'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-primary/40 hover:text-primary'}
                    `}
                    aria-pressed={isSelected}
                    title="Click to select/deselect — drag into notes to create a domain note"
                  >
                    {domain.title}
                  </button>
                );
              })}
            </div>

            {/* Notes — structured note cards with quote detection and drag-and-drop */}
            <div className="flex-1">
              <StructuredNotesEditor
                key={`${criterion.id}_current`}
                notes={val('notes_current') || []}
                onChange={(v) => onFieldChange('notes_current', v)}
                placeholder="Type a note and press Enter…"
                minHeight="15rem"
                domainId="notes_current"
                onMoveNoteIn={handleMoveNote}
              />
            </div>
          </div>
        )}
      </div>

    </div>

      {/* Bottom navigation — always pinned to the bottom of the panel */}
      <div className="flex-shrink-0 bg-white border-t border-gray-200 py-3 px-6 flex justify-between items-center">
        {prevCriterion ? (
          <button
            onClick={() => onNavigate(prevCriterion.id)}
            className="flex items-center py-2 px-4 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 hover:bg-gray-50"
          >
            <ChevronLeftIcon className="w-5 h-5 mr-2" />
            <span className="font-bold text-sm">{prevCriterion.id}</span>
          </button>
        ) : <div />}
        {nextCriterion ? (
          <button
            onClick={() => onNavigate(nextCriterion.id)}
            className="flex items-center py-2 px-4 rounded-md shadow-md bg-primary text-white hover:bg-primary/90 transition-all duration-300"
          >
            <span className="font-bold text-sm">Next: {nextCriterion.id}</span>
            <ChevronRightIcon className="w-5 h-5 ml-2" />
          </button>
        ) : (
          <button
            onClick={() => onNavigate('REVIEW')}
            className="flex items-center py-2 px-4 rounded-md shadow-md bg-primary text-white hover:bg-primary/90 transition-all duration-300"
          >
            <span className="font-bold text-sm">Go to Review</span>
            <ChevronRightIcon className="w-5 h-5 ml-2" />
          </button>
        )}
      </div>

      {/* ---- Asked-questions popup overlay ---- */}
      {showAskedPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowAskedPopup(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl p-6 w-[520px] max-w-[90vw] max-h-[80vh] overflow-y-auto text-left animate-[fadeScaleIn_0.15s_ease-out]">
            <button
              onClick={() => setShowAskedPopup(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <XIcon className="w-5 h-5" />
            </button>

            <h2 className="text-base font-bold text-gray-900 mb-0.5">
              {criterion.id}. {criterion.shortTitle}
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              Probing Questions Asked ({askedQuestionsArray.length})
            </p>

            <div className="flex flex-col gap-1.5">
              {askedQuestionsArray.map((q, idx) => (
                <div
                  key={idx}
                  className="px-2.5 py-1.5 rounded-lg text-xs font-medium italic leading-relaxed
                    bg-blue-50 text-blue-700 border border-blue-200"
                >
                  &ldquo;{q}&rdquo;
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Keyframe for popup entrance animation */}
      <style>{`
        @keyframes fadeScaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
